import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Zap, BookOpen, Lock, Table2, FlaskConical,
  BarChart2, ScrollText, Users, Settings, ChevronDown, LogOut,
  GitBranch, Menu, X, Shield, Mail, Play, BookMarked, CreditCard,
  AlertTriangle, Clock
} from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const NAV_ITEMS = [
  { path: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/app/webhooks", label: "Webhooks", icon: Zap },
  { path: "/app/complex-workflows", label: "Complex Workflows", icon: GitBranch },
  { path: "/app/api-library", label: "API Library", icon: BookOpen },
  { path: "/app/vault", label: "Vault", icon: Lock },
  { path: "/app/reference-tables", label: "Reference Tables", icon: Table2 },
  { path: "/app/blueprints", label: "Blueprints", icon: BookMarked },
  { path: "/app/subscription", label: "Subscription", icon: CreditCard },
  { path: "/app/playground", label: "Playground", icon: Play },
  { path: "/app/test-rig", label: "Test Rig", icon: FlaskConical },
  { path: "/app/analytics", label: "Analytics", icon: BarChart2 },
  { path: "/app/audit-logs", label: "Audit Logs", icon: ScrollText },
  { path: "/app/workspaces", label: "Workspaces", icon: Users },
  { path: "/app/mailbox-monitor", label: "Mailbox Monitor", icon: Mail },
  { path: "/app/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { workspaces, currentWorkspace, setCurrentWorkspaceId, isTrialActive, isTrialExpired, trialDaysLeft } = useWorkspace();
  const navigate = useNavigate();
  const [wsOpen, setWsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // ── Trial countdown banner dismiss (per workspace, per browser session) ──
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const wsId = currentWorkspace?.id;
  useEffect(() => {
    if (wsId) {
      setBannerDismissed(sessionStorage.getItem(`trial_banner_${wsId}`) === "1");
    }
  }, [wsId]);
  const dismissBanner = () => {
    if (wsId) sessionStorage.setItem(`trial_banner_${wsId}`, "1");
    setBannerDismissed(true);
  };

  // Show countdown banner for ≤7 days (matches email nudge threshold)
  const showCountdown = isTrialActive && trialDaysLeft <= 7 && !user?.is_superadmin && !bannerDismissed;
  const urgency = trialDaysLeft <= 1 ? "critical" : trialDaysLeft <= 3 ? "high" : "medium";
  const URGENCY_STYLES = {
    critical: { bar: "#EF4444", bg: "bg-red-50",    border: "border-red-200",    text: "#991B1B",  ctaBorder: "border-red-300 hover:bg-red-100"    },
    high:     { bar: "#F59E0B", bg: "bg-amber-50",  border: "border-amber-200",  text: "#92400E",  ctaBorder: "border-amber-300 hover:bg-amber-100"  },
    medium:   { bar: "#006080", bg: "bg-teal-50/60", border: "border-teal-200/70", text: "#00495f", ctaBorder: "border-teal-300 hover:bg-teal-50"     },
  };
  const us = URGENCY_STYLES[urgency];
  const countdownLabel = trialDaysLeft <= 1
    ? "less than 24 hours"
    : `${trialDaysLeft} day${trialDaysLeft !== 1 ? "s" : ""}`;

  const handleLogout = async () => {
    await logout();
    navigate("/login");  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 py-5 border-b border-[#0A1128]/15">
        <div className="flex items-center gap-2">
          <GitBranch className="w-5 h-5 text-[#0A1128]" strokeWidth={1.5} />
          <span className="font-semibold text-[#0A1128] text-lg tracking-tight">BridgeHub</span>
        </div>
        <div className="text-xs text-[#0A1128]/50 mt-0.5">API Bridge Platform</div>
      </div>

      {/* Workspace selector */}
      <div className="px-4 py-3 border-b border-[#0A1128]/15 relative">
        <button
          data-testid="workspace-selector"
          onClick={() => setWsOpen(!wsOpen)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-sm bg-[#EAE6DF] border border-[#0A1128]/10 hover:bg-[#E0DDD5] transition-colors text-sm"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Users className="w-3.5 h-3.5 text-[#0A1128]/60 flex-shrink-0" strokeWidth={1.5} />
            <span className="text-[#0A1128] font-medium truncate">{currentWorkspace?.name || "Select Workspace"}</span>
          </div>
          <ChevronDown className={`w-3.5 h-3.5 text-[#0A1128]/50 flex-shrink-0 transition-transform ${wsOpen ? "rotate-180" : ""}`} strokeWidth={1.5} />
        </button>
        {wsOpen && (
          <div className="absolute left-4 right-4 top-full mt-1 bg-white border border-[#0A1128]/15 rounded-sm shadow-lg z-50">
            {workspaces.map(ws => (
              <button
                key={ws.id}
                data-testid={`workspace-option-${ws.id}`}
                onClick={() => { setCurrentWorkspaceId(ws.id); setWsOpen(false); }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-[#F3F0EA] transition-colors ${ws.id === currentWorkspace?.id ? "text-[#0A1128] font-medium bg-[#F3F0EA]" : "text-[#0A1128]/70"}`}
              >
                {ws.name}
              </button>
            ))}
            <div className="border-t border-[#0A1128]/10">
              <button
                onClick={() => { navigate("/app/workspaces"); setWsOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs text-[#0A1128]/50 hover:bg-[#F3F0EA] transition-colors"
              >
                Manage Workspaces...
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${
                isActive
                  ? "bg-[#0A1128] text-[#F3F0EA] font-medium"
                  : "text-[#0A1128]/70 hover:bg-[#EAE6DF] hover:text-[#0A1128]"
              }`
            }
          >
            <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            {label}
          </NavLink>
        ))}
        {user?.is_superadmin && (
          <NavLink to="/app/admin"
            data-testid="nav-admin"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors mt-1 border-t border-[#0A1128]/10 ${
                isActive
                  ? "bg-[#0A1128] text-[#F3F0EA] font-medium"
                  : "text-amber-700/80 hover:bg-amber-50 hover:text-amber-700"
              }`
            }
          >
            <Shield className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
            Admin Panel
          </NavLink>
        )}
      </nav>

      {/* User footer */}
      <div className="px-4 py-4 border-t border-[#0A1128]/15">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm font-medium text-[#0A1128] truncate">{user?.name}</div>
            <div className="text-xs text-[#0A1128]/50 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="p-1.5 rounded-sm hover:bg-[#EAE6DF] text-[#0A1128]/50 hover:text-[#0A1128] transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F3F0EA] overflow-hidden">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-56 bg-white border-r border-[#0A1128]/15 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <aside className="flex flex-col w-64 bg-white border-r border-[#0A1128]/15">
            <SidebarContent />
          </aside>
          <div className="flex-1 bg-black/30" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="bg-white border-b border-[#0A1128]/15 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <button
            className="md:hidden p-1 rounded-sm hover:bg-[#EAE6DF]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden md:block" />
          <div className="flex items-center gap-3">
            {currentWorkspace && (
              <span className="text-xs text-[#0A1128]/50 border border-[#0A1128]/15 px-2 py-1 rounded-sm">
                {currentWorkspace.name}
              </span>
            )}
            <div className="w-7 h-7 bg-[#0A1128] rounded-sm flex items-center justify-center text-[#F3F0EA] text-xs font-medium">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {/* ── Trial expired — non-dismissable critical banner ── */}
          {isTrialExpired && !user?.is_superadmin && (
            <div data-testid="trial-expired-banner"
              className="relative overflow-hidden bg-red-50 border-b border-red-200 flex-shrink-0">
              <div className="h-0.5 w-full bg-red-500" />
              <div className="px-4 py-2.5 flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-red-800 font-medium">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                  Trial expired — webhook forwarding and new resource creation are disabled.
                </span>
                <a
                  href="/app/subscription"
                  className="flex-shrink-0 ml-4 text-xs font-semibold text-red-700 border border-red-300 rounded-sm px-2.5 py-1 hover:bg-red-100 transition-colors"
                >
                  Upgrade Now →
                </a>
              </div>
            </div>
          )}

          {/* ── Trial countdown — dismissable, shows ≤7 days ── */}
          <AnimatePresence>
            {showCountdown && (
              <motion.div
                key={`trial-banner-${wsId}`}
                data-testid="trial-expiring-banner"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
                className="overflow-hidden flex-shrink-0"
              >
                {/* Urgency accent bar */}
                <div className="h-0.5 w-full" style={{ backgroundColor: us.bar }} />
                <div className={`px-4 py-2.5 flex items-center justify-between text-xs border-b ${us.bg} ${us.border}`}>
                  <span className="flex items-center gap-2" style={{ color: us.text }}>
                    <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>
                      Trial for <strong>{currentWorkspace?.name}</strong> expires in{" "}
                      <strong>{countdownLabel}</strong> — upgrade to keep your webhooks running.
                    </span>
                  </span>
                  <div className="flex items-center gap-1.5 ml-4 flex-shrink-0">
                    <a
                      href="/app/subscription"
                      className={`text-xs font-semibold px-2.5 py-1 rounded-sm border transition-colors`}
                      style={{ color: us.text, borderColor: us.bar + "55" }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = us.bar + "15"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = "transparent"}
                    >
                      Upgrade Now →
                    </a>
                    <button
                      data-testid="trial-banner-dismiss"
                      onClick={dismissBanner}
                      title="Dismiss"
                      className="p-0.5 rounded transition-colors"
                      style={{ color: us.text + "80" }}
                      onMouseEnter={e => e.currentTarget.style.color = us.text}
                      onMouseLeave={e => e.currentTarget.style.color = us.text + "80"}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── No plan assigned ── */}
          {currentWorkspace && !currentWorkspace.plan_id && !isTrialActive && !isTrialExpired && !user?.is_superadmin && (
            <div data-testid="upgrade-banner" className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between text-xs flex-shrink-0">
              <span className="text-amber-800">
                This workspace has no active plan assigned. Some features may be limited.
              </span>
              <a href="/#pricing" className="text-amber-700 font-semibold hover:underline ml-4 flex-shrink-0">
                View Plans →
              </a>
            </div>
          )}
          <Outlet />
        </main>
      </div>
      <ChangePasswordModal />
    </div>
  );
}
